import { supabase } from './supabase'

export const API_BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:8000'
const IMPERSONATION_KEY = 'km_impersonation'

// ---------------------------------------------------------------------------
// Errore limite piano (HTTP 402)
// ---------------------------------------------------------------------------

export interface LimitExceededDetail {
  error: 'limit_exceeded'
  limit_type: 'uploads' | 'connectors' | 'ai_calls'
  current: number
  max: number
  upgrade_url: string
}

export interface PlanRequiredDetail {
  error: 'plan_required'
  required_plan: string
  current_plan: string
  upgrade_url: string
}

export class PlanRequiredError extends Error {
  detail: PlanRequiredDetail
  constructor(detail: PlanRequiredDetail) {
    const planLabels: Record<string, string> = {
      enterprise: 'Enterprise',
      growth: 'Growth',
      starter: 'Starter',
    }
    super(`Questa funzionalità richiede il piano ${planLabels[detail.required_plan] ?? detail.required_plan}.`)
    this.name = 'PlanRequiredError'
    this.detail = detail
  }
}

export class LimitExceededError extends Error {
  detail: LimitExceededDetail
  constructor(detail: LimitExceededDetail) {
    const labels: Record<string, string> = {
      uploads: 'upload mensili',
      connectors: 'connettori attivi',
      ai_calls: 'chiamate AI mensili',
    }
    const label = labels[detail.limit_type] ?? detail.limit_type
    const maxLabel = detail.max === 0 ? 'illimitato' : String(detail.max)
    super(`Limite ${label} raggiunto (${detail.current}/${maxLabel}). Fai upgrade per continuare.`)
    this.name = 'LimitExceededError'
    this.detail = detail
  }
}

// ---------------------------------------------------------------------------

async function authHeaders(): Promise<HeadersInit> {
  // Se è attiva una sessione di impersonazione (sessionStorage per-tab), usa quel token
  try {
    const raw = sessionStorage.getItem(IMPERSONATION_KEY)
    if (raw) {
      const state = JSON.parse(raw)
      if (state?.token && new Date(state.expires_at) > new Date()) {
        return { Authorization: `Bearer ${state.token}` }
      }
      sessionStorage.removeItem(IMPERSONATION_KEY) // scaduto
    }
  } catch {
    // sessionStorage non accessibile — prosegui con Supabase
  }

  const { data } = await supabase.auth.getSession()
  const token = data.session?.access_token
  return token ? { Authorization: `Bearer ${token}` } : {}
}

async function apiFetch<T>(path: string, init?: RequestInit, isRetry = false): Promise<T> {
  const headers = {
    'Content-Type': 'application/json',
    ...(await authHeaders()),
    ...init?.headers,
  }
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers })

  // JWT scaduto — tenta silent refresh solo per sessioni Supabase (non impersonazione)
  if (res.status === 401 && !isRetry && !sessionStorage.getItem(IMPERSONATION_KEY)) {
    const { data, error } = await supabase.auth.refreshSession()
    if (error || !data.session) {
      // Refresh fallito: disconnette e lascia che ProtectedRoute reindirizzi a /login
      await supabase.auth.signOut()
      throw new Error('Sessione scaduta. Accedi di nuovo.')
    }
    // Retry con il nuovo access_token
    return apiFetch<T>(path, init, true)
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    // 402 Payment Required — limite piano raggiunto
    if (res.status === 402 && body?.detail?.error === 'limit_exceeded') {
      throw new LimitExceededError(body.detail as LimitExceededDetail)
    }
    // 402 Payment Required — piano insufficiente per la feature
    if (res.status === 402 && body?.detail?.error === 'plan_required') {
      throw new PlanRequiredError(body.detail as PlanRequiredDetail)
    }
    throw new Error(body?.detail ?? `API error ${res.status}`)
  }
  // 204 No Content — no body to parse
  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

export const api = {
  get: <T>(path: string) => apiFetch<T>(path),
  post: <T>(path: string, body: unknown) =>
    apiFetch<T>(path, { method: 'POST', body: JSON.stringify(body) }),

  patch: <T>(path: string, body: unknown) =>
    apiFetch<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) =>
    apiFetch<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  delete: <T>(path: string) => apiFetch<T>(path, { method: 'DELETE' }),
}

/**
 * Scarica un file binario dal backend e avvia il download nel browser.
 * Gestisce autenticazione e impersonazione come apiFetch.
 */
export async function downloadBlob(path: string, filename: string): Promise<void> {
  const headers = await authHeaders()
  const res = await fetch(`${API_BASE}${path}`, { headers: headers as HeadersInit })
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new Error(body?.detail ?? `Errore download ${res.status}`)
  }
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
